import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import IconX from '@/assets/icons/icon_x.svg';
import IconYoutube from '@/assets/icons/icon_youtube.svg';
import DmmComLogo from '@/assets/icons/logo_dmmcom.svg';
import Anchor from '@/layouts/Footer/Anchor';
import { getKeirinHelpArticleUrl, keirinHelpUrl } from '@/lib/dmm/help';
import { areas, velodromeAreasMap } from '@/lib/race';
import { Area } from '@/modelTypes';

import BreadcrumbNav from '../BreadcrumbNav';
import {
  Accounts,
  AsideWrapper,
  CompanyLinkList,
  CompanyLogo,
  Copyright,
  FooterWrapper,
  GuideLinkList,
  Item,
  ItemName,
  LinkList,
  OfficialAccountWrapper,
  TextBold,
} from './style';

const keirinInfoLinks = [
  {
    text: '競輪場一覧',
    to: '/velodromes',
  },
  {
    text: '競輪 開催スケジュール',
    to: '/calendar',
  },
  {
    text: '競輪 グレードレース開催スケジュール',
    to: '/graderace',
  },
  {
    text: '競輪 出走表',
    to: '/racecard',
  },
  {
    text: '競輪 レース結果',
    to: '/results',
  },
];

const guideLinks = [
  {
    text: 'DMM競輪とは？',
    to: '/guide',
  },
  {
    text: 'ご利用のながれ',
    to: '/guide/flow',
  },
  {
    text: 'toreta+ とは？',
    to: '/guide/toreta',
  },
  {
    text: '入金/払戻金 残高とは？',
    to: '/guide/wallet',
  },
  {
    text: '投票方法',
    to: '/guide/voting',
  },
  {
    text: '出走表の見方',
    to: '/guide/racecard',
  },
  {
    text: '競輪用語集',
    to: '/glossary',
  },
  {
    text: 'よくある質問',
    to: keirinHelpUrl,
  },
  {
    text: 'お問い合わせ',
    to: import.meta.env.VITE_DMM_INQUIRY_URL,
  },
];

const termsLinks = [
  {
    text: 'DMM競輪利用規約',
    to: `${import.meta.env.VITE_DMM_TERMS_URL}/chariloto/`,
  },
  {
    text: 'toreta+ 利用規約',
    to: `${import.meta.env.VITE_DMM_TERMS_URL}/toretaplus/`,
  },
  {
    text: '特定商取引法に基づく表示',
    to: `${import.meta.env.VITE_DMM_TERMS_URL}/keirin_tokushou/`,
  },
];

const gambleLinks = [
  {
    text: '車券の購入は20歳になってから。競輪は適度に楽しみましょう',
    to: '/news/info_shaken20/',
  },
  {
    text: '車券の購入にのめり込む不安のある方のご相談',
    to: '/news/info_keirinconsultation',
  },
  {
    text: '公営競技の払戻金の支払を受けた方へ',
    to: getKeirinHelpArticleUrl(31129),
  },
  {
    text: 'ギャンブル依存症セルフチェック',
    to: '/news/info_selfchecktool',
  },
];

const companyLinks = [
  {
    text: '会社概要',
    to: `${import.meta.env.VITE_DMM_TERMS_URL}/profile/`,
  },
  {
    text: 'プレスリリース',
    to: 'https://dmm-corp.com/press/',
  },
  {
    text: 'DMM会員規約',
    to: `${import.meta.env.VITE_DMM_TERMS_URL}/member/`,
  },
  {
    text: '個人情報保護に関して',
    to: `${import.meta.env.VITE_DMM_TERMS_URL}/privacy/`,
  },
  {
    text: '特定商取引法に基づく表示',
    to: `${import.meta.env.VITE_DMM_TERMS_URL}/commerce/`,
  },
  {
    text: '特別採用・事業譲渡(M&A)に関して',
    to: 'https://dmm-corp.com/business/partnership/',
  },
  {
    text: '採用情報',
    to: 'https://dmm-corp.com/recruit/',
  },
  {
    text: 'ビジネスパートナー募集',
    to: `${import.meta.env.VITE_DMM_TERMS_URL}/partner/`,
  },
  {
    text: 'メールマガジン',
    to: import.meta.env.VITE_DMM_MAIL_URL,
  },
];

const Footer: React.FC = () => {
  return (
    <div>
      <BreadcrumbNav />
      <AsideWrapper>
        <OfficialAccountWrapper>
          <TextBold>DMM競輪 公式アカウント</TextBold>
          <Accounts>
            <a href="https://x.com/dmm_keirin" target="_blank" rel="noreferrer">
              <img src={IconX} alt="Xアイコン" width="48" height="48" />
            </a>
            <a
              href="https://www.youtube.com/channel/UCmiXL_z9owqynl0pFQ8XjTA"
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={IconYoutube}
                alt="Youtubeアイコン"
                width="48"
                height="48"
              />
            </a>
          </Accounts>
        </OfficialAccountWrapper>
        <Item>
          <ItemName>競輪情報</ItemName>
          <LinkList isOrganized>
            {keirinInfoLinks.map(kil => (
              <li key={kil.text}>
                <Anchor to={kil.to}>{kil.text}</Anchor>
              </li>
            ))}
          </LinkList>
        </Item>
        <Item>
          <ItemName>競輪場一覧</ItemName>
          {(Object.keys(velodromeAreasMap) as Area[]).map(key => (
            <Fragment key={key}>
              <ItemName>{areas[key]}</ItemName>
              <LinkList>
                {velodromeAreasMap[key].map(v => (
                  <li key={v.romanName}>
                    <Link to={`/${v.romanName}`}>{v.name}競輪</Link>
                  </li>
                ))}
              </LinkList>
            </Fragment>
          ))}
        </Item>
        <Item>
          <ItemName>DMM競輪 ご利用ガイド</ItemName>
          <LinkList isOrganized>
            {guideLinks.map(gl => (
              <li key={gl.text}>
                <Anchor to={gl.to}>{gl.text}</Anchor>
              </li>
            ))}
          </LinkList>
        </Item>
        <Item>
          <ItemName>DMM競輪 規約</ItemName>
          <LinkList isOrganized>
            {termsLinks.map(tl => (
              <li key={tl.text}>
                <Anchor to={tl.to}>{tl.text}</Anchor>
              </li>
            ))}
          </LinkList>
        </Item>
        <GuideLinkList isOrganized>
          {gambleLinks.map(gl => (
            <li key={gl.text}>
              <Anchor to={gl.to}>{gl.text}</Anchor>
            </li>
          ))}
        </GuideLinkList>
      </AsideWrapper>
      <FooterWrapper>
        <div>
          <CompanyLogo src={DmmComLogo} alt="DMM.com" />
          <CompanyLinkList>
            {companyLinks.map(cl => (
              <li key={cl.text}>
                <Anchor to={cl.to}>{cl.text}</Anchor>
              </li>
            ))}
          </CompanyLinkList>
          <Copyright>
            Copyright &copy; since 1998 DMM All Rights Reserved.
          </Copyright>
        </div>
      </FooterWrapper>
    </div>
  );
};

export default Footer;
